import { Input, type InputProps, type InputRef } from 'antd';
import { useMemo } from 'react';
import { IMask } from 'react-imask';

interface IProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'defaultValue'
> {
  /** Маска imask, например `INPUT_MASKS.iin`. */
  mask: string;
  /** Значение без маски: только введённые символы (`930615401987`). */
  value?: string;
  onChange?: (unmaskedValue: string) => void;
  /** Подготовка сырого ввода перед маской — например, очистка вставленного номера целиком. */
  normalize?: (rawValue: string) => string;
  /** Ref на antd Input: форма фокусирует поле с ошибкой. */
  ref?: React.Ref<InputRef>;
}

/**
 * antd Input с маской ввода. Наружу отдаёт и принимает значение без маски,
 * поэтому в форме хранятся «чистые» цифры, а сотрудник видит отформатированный ввод.
 *
 * Поле контролируемое: сырой ввод прогоняется через маску на каждое изменение.
 * imask напрямую на DOM не вешаем — antd Input сам управляет value и перетирал бы маску.
 */
const MaskedInput: React.FC<IProps> = ({
  mask,
  value = '',
  onChange,
  normalize,
  ref,
  ...inputProps
}) => {
  const maskedValue = useMemo(() => {
    const masked = IMask.createMask({ mask });
    masked.unmaskedValue = value;
    return masked.value;
  }, [mask, value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const masked = IMask.createMask({ mask });
    masked.resolve(
      normalize ? normalize(event.target.value) : event.target.value
    );
    onChange?.(masked.unmaskedValue);
  };

  return (
    <Input
      {...inputProps}
      ref={ref}
      inputMode={inputProps.inputMode ?? 'numeric'}
      value={maskedValue}
      onChange={handleChange}
    />
  );
};

export default MaskedInput;
